import { client } from "../supabase";

// Asociación de pagos de CapEx (adelantos) a una factura recibida — Fase 2.
// Toda la escritura es ATÓMICA en el servidor (RPCs plpgsql, migración
// 20260814_asociar_pagos_capex_factura.sql). Acá solo se orquesta la llamada: si la RPC lanza
// EXCEPTION (guard a-caballo, idempotencia, activo no candidato, etc.) revierte TODO y el error
// llega como `error.message` para mostrarlo.

/**
 * Asocia los pagos CapEx (por movimiento_bancario_id) a una factura recibida.
 * @param {number}   facturaId    - id en facturas_sii_recibidas.
 * @param {number[]} bancarioIds  - movimiento_bancario_id de los pagos tildados.
 * @param {number}   usuarioId    - id del usuario real (autoría, 10.104).
 * @returns {Promise<object>} JSON de la RPC (asociacion_id, mbf_creadas, activos_ligados, …).
 */
export const asociarPagosCapex = async (facturaId, bancarioIds, usuarioId) => {
  const { data, error } = await client.rpc("asociar_pagos_capex_a_factura", {
    p_factura_id: facturaId,
    p_bancario_ids: bancarioIds,
    p_usuario_id: usuarioId ?? null,
  });
  if (error) throw error;
  return data;
};

/**
 * Revierte la asociación activa de una factura (deshace MBF + re-tag + ligado de activos + plazopago).
 * @param {number} facturaId
 * @param {number} usuarioId
 * @returns {Promise<object>} JSON de la RPC.
 */
export const desasociarPagosCapex = async (facturaId, usuarioId) => {
  const { data, error } = await client.rpc("desasociar_pagos_capex_de_factura", {
    p_factura_id: facturaId,
    p_usuario_id: usuarioId ?? null,
  });
  if (error) throw error;
  return data;
};

/**
 * La asociación ACTIVA de una factura, o null si no tiene. Se usa para que el modal muestre el
 * estado "ya asociada" (con opción de desasociar) en vez del listado de candidatos.
 * @param {number} facturaId
 * @returns {Promise<object|null>}
 */
export const getAsociacionActiva = async (facturaId) => {
  const { data, error } = await client
    .from("asociaciones_capex_factura")
    .select("id, factura_sii_recibida_id, bancario_ids, activo_ids, created_at")
    .eq("factura_sii_recibida_id", facturaId)
    .eq("estado", "activa")
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

/**
 * Set de factura_sii_recibida_id que tienen una asociación CapEx ACTIVA en un país. Batch para que
 * las listas (kebab de Compras, "Por pagar" de Flujo) decidan por ID si una factura está asociada
 * (→ ofrecer "Desasociar") sin una query por fila. Comparación por ID (10.7).
 * @param {string} pais
 * @returns {Promise<Set<number>>}
 */
export const getFacturasConAsociacionCapex = async (pais) => {
  const { data, error } = await client
    .from("asociaciones_capex_factura")
    .select("factura_sii_recibida_id")
    .eq("estado", "activa")
    .eq("pais", pais);
  if (error) throw error;
  return new Set((data || []).map((r) => r.factura_sii_recibida_id));
};
